import { RecentlyViewed } from '@theme/recently-viewed-products';
import { fetchProductCards } from '@theme/mineralia-product-list';
import '@theme/mineralia-wishlist';

/**
 * <mineralia-recently-viewed data-limit="8" data-exclude="<product id>">
 * Renders the products stored by RecentlyViewed (localStorage) with the Section Rendering API.
 * Stays hidden when there is nothing to show.
 */
class MineraliaRecentlyViewed extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    if (this.#abort.signal.aborted) this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelector('[data-rv-clear]')?.addEventListener('click', this.#clear, { signal });
    this.querySelector('[data-rv-prev]')?.addEventListener('click', () => this.#scrollBy(-1), { signal });
    this.querySelector('[data-rv-next]')?.addEventListener('click', () => this.#scrollBy(1), { signal });
    this.#list?.addEventListener('scroll', this.#updateNav, { passive: true, signal });
    window.addEventListener('resize', this.#updateNav, { passive: true, signal });

    this.#load();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get #list() {
    return this.querySelector('[data-rv-list]');
  }

  async #load() {
    const limit = Number(this.dataset.limit || 8);
    const exclude = String(this.dataset.exclude || '');
    const ids = RecentlyViewed.getProducts(12).filter((id) => id !== exclude).slice(0, limit);
    if (ids.length === 0) return;

    try {
      const cards = await fetchProductCards(ids, 'mineralia-recently-viewed', this.#abort.signal);
      const list = this.#list;
      if (!list || cards.length === 0) return;

      cards.forEach((card, index) => card.style.setProperty('--rail-index', String(index)));
      list.replaceChildren(...cards);
      this.hidden = false;
      this.toggleAttribute('data-ready', true);
      requestAnimationFrame(this.#updateNav);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[mineralia-recently-viewed]', error);
    }
  }

  #clear = () => {
    RecentlyViewed.clearProducts();
    this.hidden = true;
    this.#list?.replaceChildren();
  };

  /** @param {number} direction */
  #scrollBy(direction) {
    const list = this.#list;
    if (!list) return;
    list.scrollBy({ left: direction * list.clientWidth * 0.9 });
  }

  #updateNav = () => {
    const list = this.#list;
    const nav = this.querySelector('[data-rv-nav]');
    if (!list || !(nav instanceof HTMLElement)) return;

    const overflow = list.scrollWidth - list.clientWidth > 4;
    nav.hidden = !overflow || window.matchMedia('(max-width: 749px)').matches;
    const prev = this.querySelector('[data-rv-prev]');
    const next = this.querySelector('[data-rv-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = list.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
  };
}

if (!customElements.get('mineralia-recently-viewed')) {
  customElements.define('mineralia-recently-viewed', MineraliaRecentlyViewed);
}
